/**
 * WaitlistView — Phase F.1 owner-side view.
 *
 * Lets the Black Ridge team see the maintenance-plan waitlist:
 *   - Summary tiles (totals + projected revenue)
 *   - By-tier breakdown
 *   - Filterable list (status / tier / property type / search)
 *   - Per-entry drawer with contact + convert actions + notes
 *   - CSV export
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CalendarClock,
  Check,
  CheckCircle2,
  ChevronRight,
  Crown,
  Download,
  Hourglass,
  Loader2,
  Mail,
  MessageSquare,
  Phone,
  RefreshCw,
  Search,
  Sparkles,
  Trash2,
  UserCheck,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { crmApi } from "@/lib/ownerClient";

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "contacted", label: "Contacted" },
  { value: "ready_to_convert", label: "Ready to convert" },
  { value: "converted", label: "Converted" },
  { value: "passed", label: "Passed" },
];

const TIER_LABELS = {
  "roofwatch-residential": "RoofWatch Residential",
  "homecare-essential": "HomeCare Essential",
  "homecare-plus": "HomeCare Plus",
  "homecare-reserve": "HomeCare Reserve",
  "propertycare-core": "PropertyCare Core",
  "propertycare-pro": "PropertyCare Pro",
  "portfolio-reserve": "Portfolio Reserve",
  "roofwatch-commercial": "RoofWatch Commercial",
  undecided: "Help me choose",
};

const STATUS_TONE = {
  pending: "border-amber-500/40 text-amber-200",
  contacted: "border-blue-500/40 text-blue-200",
  ready_to_convert: "border-violet-500/40 text-violet-200",
  converted: "border-emerald-500/40 text-emerald-200",
  passed: "border-zinc-700 text-zinc-400",
};

function dollarFromCents(cents) {
  if (!cents || cents <= 0) return "—";
  return `$${(cents / 100).toFixed(0)}`;
}

function StatusBadge({ status }) {
  return (
    <Badge
      variant="outline"
      className={`text-[10px] py-0 ${STATUS_TONE[status] || "border-zinc-700 text-zinc-400"}`}
    >
      {status.replace(/_/g, " ")}
    </Badge>
  );
}

function Kpi({ icon: Icon, label, value, hint, tone = "default" }) {
  const toneCls =
    tone === "pass"
      ? "text-emerald-300"
      : tone === "warn"
      ? "text-amber-300"
      : "text-white";
  return (
    <Card className="bg-zinc-950 border-zinc-800">
      <CardContent className="p-3">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-zinc-500">
          <Icon className="h-3 w-3" />
          {label}
        </div>
        <div className={`text-2xl font-semibold mt-1 ${toneCls}`}>{value}</div>
        {hint ? <div className="text-[10.5px] text-zinc-500 mt-0.5">{hint}</div> : null}
      </CardContent>
    </Card>
  );
}

export default function WaitlistView() {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [tierFilter, setTierFilter] = useState("all");
  const [propertyFilter, setPropertyFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [openEntry, setOpenEntry] = useState(null);

  const reload = useCallback(
    async (refreshOnly = false) => {
      if (refreshOnly) setRefreshing(true);
      else setLoading(true);
      try {
        const [listRes, summaryRes] = await Promise.all([
          crmApi.listWaitlist({
            status: statusFilter !== "all" ? statusFilter : undefined,
            preferred_tier: tierFilter !== "all" ? tierFilter : undefined,
            property_type: propertyFilter !== "all" ? propertyFilter : undefined,
            q: search || undefined,
            limit: 200,
          }),
          crmApi.waitlistSummary(),
        ]);
        setEntries(listRes.entries || []);
        setTotal(listRes.total || 0);
        setSummary(summaryRes);
      } catch (err) {
        toast.error(err.message || "Failed to load waitlist");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [statusFilter, tierFilter, propertyFilter, search],
  );

  useEffect(() => {
    reload();
  }, [reload]);

  function onSearchSubmit(e) {
    e.preventDefault();
    setSearch(searchInput.trim());
  }

  function onCsvExport() {
    window.open(crmApi.waitlistCsvUrl(), "_blank");
  }

  const tiers = summary?.by_tier || [];
  const totals = summary?.totals || {};

  return (
    <div className="space-y-6" data-testid="owner-waitlist-view">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-white">
            <Hourglass className="h-5 w-5 text-amber-400" />
            Maintenance Plan Waitlist
          </h1>
          <p className="text-sm text-zinc-400">
            Homeowners holding their spot for the July 2026 HomeCare launch.
            No charge today &mdash; activation + inspection fees collected after Stripe LIVE goes live.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={onCsvExport}
            className="border-zinc-800 text-white hover:bg-zinc-900"
            data-testid="waitlist-export-csv"
          >
            <Download className="h-3.5 w-3.5 mr-1.5" />
            CSV
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => reload(true)}
            disabled={refreshing}
            className="border-zinc-800 text-white hover:bg-zinc-900"
            data-testid="waitlist-refresh"
          >
            {refreshing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi icon={Sparkles} label="Total on list" value={totals.total ?? 0} />
        <Kpi icon={Hourglass} label="Pending" value={totals.pending ?? 0} tone="warn" />
        <Kpi icon={MessageSquare} label="Contacted" value={totals.contacted ?? 0} />
        <Kpi icon={UserCheck} label="Ready to convert" value={totals.ready_to_convert ?? 0} />
        <Kpi
          icon={Crown}
          label="Projected inspection $"
          value={dollarFromCents(totals.total_inspection_value_cents ?? 0)}
          hint="Sum of locked-in inspection fees"
          tone="pass"
        />
        <Kpi
          icon={CalendarClock}
          label="Projected MRR"
          value={dollarFromCents(totals.total_monthly_value_cents ?? 0)}
          hint="If everyone activates at quoted monthly"
          tone="pass"
        />
      </div>

      {/* By-tier breakdown */}
      {tiers.length > 0 ? (
        <Card className="bg-zinc-950 border-zinc-800">
          <CardContent className="p-4">
            <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-2">
              By preferred tier
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {tiers.map((t) => (
                <div
                  key={t.tier}
                  className="rounded border border-zinc-800 bg-black p-3"
                  data-testid={`waitlist-tier-${t.tier}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-white text-[13px]">{t.tier_label}</span>
                    <Badge
                      variant="outline"
                      className="border-amber-500/40 text-amber-200 text-[10px]"
                    >
                      {t.count}
                    </Badge>
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-1.5">
                    inspect {dollarFromCents(t.inspection_value_cents)} · mrr{" "}
                    {dollarFromCents(t.monthly_value_cents)}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* Filters */}
      <Card className="bg-zinc-950 border-zinc-800">
        <CardContent className="p-3 space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <div className="w-[180px]">
              <Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1 block">
                Status
              </Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger
                  className="h-9 bg-zinc-950 border-zinc-800 text-white"
                  data-testid="waitlist-filter-status"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-[220px]">
              <Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1 block">
                Tier
              </Label>
              <Select value={tierFilter} onValueChange={setTierFilter}>
                <SelectTrigger
                  className="h-9 bg-zinc-950 border-zinc-800 text-white"
                  data-testid="waitlist-filter-tier"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All tiers</SelectItem>
                  {Object.keys(TIER_LABELS).map((t) => (
                    <SelectItem key={t} value={t}>
                      {TIER_LABELS[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-[160px]">
              <Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1 block">
                Property type
              </Label>
              <Select value={propertyFilter} onValueChange={setPropertyFilter}>
                <SelectTrigger
                  className="h-9 bg-zinc-950 border-zinc-800 text-white"
                  data-testid="waitlist-filter-property"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="residential">Residential</SelectItem>
                  <SelectItem value="commercial">Commercial</SelectItem>
                  <SelectItem value="unknown">Unknown</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <form onSubmit={onSearchSubmit} className="flex items-end gap-2 flex-1 min-w-[260px]">
              <div className="flex-1">
                <Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1 block">
                  Search
                </Label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
                  <Input
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="name, email, phone, address…"
                    className="h-9 pl-8 bg-black border-zinc-800 text-white placeholder:text-zinc-600"
                    data-testid="waitlist-search-input"
                  />
                </div>
              </div>
              <Button type="submit" className="h-9 bg-amber-500 hover:bg-amber-400 text-black">
                Go
              </Button>
              {search ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setSearchInput("");
                    setSearch("");
                  }}
                  className="h-9 text-zinc-400 hover:text-white"
                >
                  Clear
                </Button>
              ) : null}
            </form>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-zinc-950 border-zinc-800">
        <CardContent className="p-0">
          <div className="grid grid-cols-12 text-[10px] uppercase tracking-[0.16em] text-zinc-500 px-4 py-2 border-b border-zinc-800">
            <div className="col-span-3">Name / Email</div>
            <div className="col-span-2">Tier</div>
            <div className="col-span-1 text-right">SqFt</div>
            <div className="col-span-2 text-right">Inspection / Monthly</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2 text-right">Joined</div>
          </div>
          {loading ? (
            <div className="py-10 flex items-center justify-center text-zinc-500">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
            </div>
          ) : null}
          {!loading && entries.length === 0 ? (
            <div className="py-12 text-center" data-testid="waitlist-empty">
              <Hourglass className="h-6 w-6 text-amber-400 mx-auto mb-2" />
              <div className="text-white text-sm font-medium">No waitlist entries yet</div>
              <div className="text-zinc-500 text-[12px] mt-1">
                When homeowners sign up on /maintenance-plans they&rsquo;ll appear here.
              </div>
            </div>
          ) : null}
          {!loading &&
            entries.map((e) => (
              <button
                type="button"
                key={e.id}
                onClick={() => setOpenEntry(e.id)}
                className="w-full grid grid-cols-12 items-center px-4 py-3 border-b border-zinc-900 text-left hover:bg-zinc-900/60 transition-colors"
                data-testid={`waitlist-row-${e.id}`}
              >
                <div className="col-span-3 min-w-0">
                  <div className="text-white font-medium truncate">{e.name || "(no name)"}</div>
                  <div className="text-[11px] text-zinc-500 truncate">{e.email}</div>
                </div>
                <div className="col-span-2 text-[12px] text-zinc-300">
                  {TIER_LABELS[e.preferred_tier] || e.preferred_tier}
                </div>
                <div className="col-span-1 text-right text-[12px] text-zinc-400">
                  {e.sqft || "—"}
                </div>
                <div className="col-span-2 text-right text-[12px]">
                  <span className="text-amber-200">{dollarFromCents(e.inspection_fee_cents)}</span>
                  <span className="text-zinc-600 mx-1">·</span>
                  <span className="text-emerald-200">{dollarFromCents(e.monthly_price_cents)}</span>
                </div>
                <div className="col-span-2">
                  <StatusBadge status={e.status} />
                </div>
                <div className="col-span-2 text-right text-[11px] text-zinc-500 flex items-center justify-end gap-2">
                  {e.created_at
                    ? new Date(e.created_at).toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                      })
                    : "—"}
                  <ChevronRight className="h-3.5 w-3.5 text-zinc-600" />
                </div>
              </button>
            ))}
        </CardContent>
      </Card>

      <div className="text-[11px] text-zinc-600 text-right">
        Showing {entries.length} of {total}
      </div>

      <WaitlistEntryDrawer
        entryId={openEntry}
        onClose={() => setOpenEntry(null)}
        onUpdated={() => reload(true)}
      />
    </div>
  );
}

function WaitlistEntryDrawer({ entryId, onClose, onUpdated }) {
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyAction, setBusyAction] = useState("");
  const [statusOverride, setStatusOverride] = useState("");
  const [contactNote, setContactNote] = useState("");
  const [convertNote, setConvertNote] = useState("");
  const [internalNotes, setInternalNotes] = useState("");

  const open = Boolean(entryId);

  useEffect(() => {
    if (!entryId) {
      setEntry(null);
      setStatusOverride("");
      setContactNote("");
      setConvertNote("");
      setInternalNotes("");
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const e = await crmApi.getWaitlistEntry(entryId);
        if (cancelled) return;
        setEntry(e);
        setStatusOverride(e.status || "pending");
        setInternalNotes(e.notes || "");
      } catch (err) {
        toast.error(err.message || "Failed to load entry");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [entryId]);

  async function runContact() {
    if (!entryId) return;
    setBusyAction("contact");
    try {
      const updated = await crmApi.contactWaitlistEntry(entryId, {
        note: contactNote.trim(),
        set_status: "contacted",
      });
      setEntry(updated);
      setInternalNotes(updated.notes || "");
      setContactNote("");
      toast.success("Marked contacted");
      onUpdated?.();
    } catch (err) {
      toast.error(err.message || "Contact failed");
    } finally {
      setBusyAction("");
    }
  }

  async function runConvert() {
    if (!entryId) return;
    setBusyAction("convert");
    try {
      const updated = await crmApi.convertWaitlistEntry(entryId, {
        note: convertNote.trim(),
      });
      setEntry(updated);
      setInternalNotes(updated.notes || "");
      setConvertNote("");
      toast.success("Marked converted 🎉");
      onUpdated?.();
    } catch (err) {
      toast.error(err.message || "Convert failed");
    } finally {
      setBusyAction("");
    }
  }

  async function saveStatus() {
    if (!entryId) return;
    setBusyAction("status");
    try {
      const updated = await crmApi.updateWaitlistEntry(entryId, {
        status: statusOverride,
        notes: internalNotes,
      });
      setEntry(updated);
      toast.success("Saved");
      onUpdated?.();
    } catch (err) {
      toast.error(err.message || "Save failed");
    } finally {
      setBusyAction("");
    }
  }

  async function runDelete() {
    if (!entryId) return;
    if (!window.confirm("Delete this waitlist entry? This cannot be undone.")) return;
    setBusyAction("delete");
    try {
      await crmApi.deleteWaitlistEntry(entryId);
      toast.success("Entry deleted");
      onUpdated?.();
      onClose?.();
    } catch (err) {
      toast.error(err.message || "Delete failed");
    } finally {
      setBusyAction("");
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="dark max-w-[760px] bg-black border-zinc-800 text-white overflow-y-auto max-h-[90vh]"
        data-testid="waitlist-detail-drawer"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Hourglass className="h-4 w-4 text-amber-400" />
            Waitlist entry
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Contact info, locked pricing, and post-launch conversion actions.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex items-center justify-center text-zinc-500">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
          </div>
        ) : null}

        {!loading && entry ? (
          <div className="space-y-4 mt-2">
            <div className="grid sm:grid-cols-2 gap-3">
              <InfoRow icon={UserCheck} label="Name" value={entry.name || "—"} />
              <InfoRow
                icon={Mail}
                label="Email"
                value={
                  <a href={`mailto:${entry.email}`} className="text-amber-300 hover:underline">
                    {entry.email}
                  </a>
                }
              />
              <InfoRow icon={Phone} label="Phone" value={entry.phone || "—"} />
              <InfoRow
                icon={CalendarClock}
                label="Joined"
                value={
                  entry.created_at
                    ? new Date(entry.created_at).toLocaleString(undefined, {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : "—"
                }
              />
              <InfoRow
                icon={Crown}
                label="Held tier"
                value={TIER_LABELS[entry.preferred_tier] || entry.preferred_tier}
              />
              <InfoRow icon={Sparkles} label="Sqft" value={entry.sqft || "—"} />
            </div>

            <div className="grid sm:grid-cols-3 gap-3">
              <Card className="bg-zinc-950 border-amber-500/30">
                <CardContent className="p-3">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-amber-200">
                    Inspection fee (locked)
                  </div>
                  <div className="text-amber-100 text-xl font-semibold mt-1">
                    {dollarFromCents(entry.inspection_fee_cents)}
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-zinc-950 border-emerald-500/30">
                <CardContent className="p-3">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-emerald-200">
                    Target monthly
                  </div>
                  <div className="text-emerald-100 text-xl font-semibold mt-1">
                    {dollarFromCents(entry.monthly_price_cents)}
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-zinc-950 border-zinc-800">
                <CardContent className="p-3">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                    Status / nurture
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-[11.5px]">
                    <StatusBadge status={entry.status} />
                  </div>
                  <div className="text-[10.5px] text-zinc-500 mt-1">
                    confirm: {entry.confirm_email_status} · nurture: {entry.nurture_status}
                  </div>
                </CardContent>
              </Card>
            </div>

            {entry.address ? (
              <Card className="bg-zinc-950 border-zinc-800">
                <CardContent className="p-3 text-[12.5px]">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1">
                    Address
                  </div>
                  <div className="text-zinc-200">
                    {entry.address}
                    {entry.city ? `, ${entry.city}` : ""}
                    {entry.state ? `, ${entry.state}` : ""}
                    {entry.zip_code ? ` ${entry.zip_code}` : ""}
                  </div>
                </CardContent>
              </Card>
            ) : null}

            {entry.interest_note ? (
              <Card className="bg-zinc-950 border-zinc-800">
                <CardContent className="p-3 text-[12.5px]">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1">
                    What got them interested
                  </div>
                  <div className="text-zinc-200 whitespace-pre-wrap">{entry.interest_note}</div>
                </CardContent>
              </Card>
            ) : null}

            {/* Internal notes + status */}
            <Card className="bg-zinc-950 border-zinc-800">
              <CardContent className="p-3 space-y-2">
                <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                  Internal notes
                </div>
                <Textarea
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  rows={3}
                  className="bg-black border-zinc-800 text-white placeholder:text-zinc-600"
                  placeholder="Owner-only notes (not visible to the customer)"
                  data-testid="waitlist-internal-notes"
                />
                <div className="flex items-end gap-2">
                  <div className="w-[200px]">
                    <Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1 block">
                      Status
                    </Label>
                    <Select value={statusOverride} onValueChange={setStatusOverride}>
                      <SelectTrigger className="h-9 bg-zinc-950 border-zinc-800 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.filter((s) => s.value !== "all").map((o) => (
                          <SelectItem key={o.value} value={o.value}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    type="button"
                    onClick={saveStatus}
                    disabled={busyAction === "status"}
                    className="bg-amber-500 hover:bg-amber-400 text-black h-9"
                    data-testid="waitlist-save-status"
                  >
                    {busyAction === "status" ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                    ) : (
                      <Check className="h-4 w-4 mr-1.5" />
                    )}
                    Save
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Contact action */}
            <Card className="bg-zinc-950 border-blue-500/30">
              <CardContent className="p-3 space-y-2">
                <div className="text-[10px] uppercase tracking-[0.16em] text-blue-200">
                  Mark contacted
                </div>
                <Textarea
                  value={contactNote}
                  onChange={(e) => setContactNote(e.target.value)}
                  rows={2}
                  className="bg-black border-zinc-800 text-white placeholder:text-zinc-600"
                  placeholder='e.g. "Called and confirmed July launch — wants HomeCare Plus."'
                  data-testid="waitlist-contact-note"
                />
                <Button
                  type="button"
                  onClick={runContact}
                  disabled={busyAction === "contact"}
                  variant="outline"
                  className="border-blue-500/40 text-blue-200 hover:bg-blue-500/10 h-9"
                  data-testid="waitlist-contact-run"
                >
                  {busyAction === "contact" ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                  ) : (
                    <MessageSquare className="h-4 w-4 mr-1.5" />
                  )}
                  Mark contacted
                </Button>
              </CardContent>
            </Card>

            {/* Convert action */}
            <Card className="bg-zinc-950 border-emerald-500/30">
              <CardContent className="p-3 space-y-2">
                <div className="text-[10px] uppercase tracking-[0.16em] text-emerald-200">
                  Convert to active member
                </div>
                <Textarea
                  value={convertNote}
                  onChange={(e) => setConvertNote(e.target.value)}
                  rows={2}
                  className="bg-black border-zinc-800 text-white placeholder:text-zinc-600"
                  placeholder='e.g. "Activated HomeCare Plus — baseline booked for 7/12."'
                  data-testid="waitlist-convert-note"
                />
                <Button
                  type="button"
                  onClick={runConvert}
                  disabled={busyAction === "convert"}
                  className="bg-emerald-500 hover:bg-emerald-400 text-black h-9"
                  data-testid="waitlist-convert-run"
                >
                  {busyAction === "convert" ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-1.5" />
                  )}
                  Mark converted
                </Button>
              </CardContent>
            </Card>
          </div>
        ) : null}

        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            onClick={runDelete}
            disabled={busyAction === "delete"}
            className="text-red-300 hover:bg-red-500/10"
            data-testid="waitlist-delete"
          >
            {busyAction === "delete" ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
            ) : (
              <Trash2 className="h-4 w-4 mr-1.5" />
            )}
            Delete
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="border-zinc-700 text-white hover:bg-zinc-900"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2 text-[12.5px]">
      <Icon className="h-3.5 w-3.5 text-zinc-500 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500">{label}</div>
        <div className="text-white mt-0.5 truncate">{value}</div>
      </div>
    </div>
  );
}
